/**
 * Copyright 2025 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 **/
import React from 'react'
import { View, Text } from '@az/starc-ui'

import { useTranslation } from 'react-i18next'
import { Item, ItemSlot } from '../../types'
import {
  computeAisle,
  computeSlot,
  computeZone,
  padTo9LeadingZeroes,
  stripLeadingZeroes
} from '../../util'
import style from './PartSearchResultCard.module.css'

interface ItemsResultProps {
  searchTerm: string
  items: Item[]
}
const PartSearchCard: React.FC<ItemsResultProps> = props => {
  const { items, searchTerm } = props
  const numberOfItems = items.length
  const { t } = useTranslation()
  return (
    <View padding={[2, 5]}>
      <View padding={[3, 0]}>
        {searchTerm && (
          <Text variant="large-body" color="300" size="137">
            {numberOfItems} {t('items-found')} &quot;{searchTerm}&quot;
          </Text>
        )}
      </View>
      {items.map(item => {
        return (
          <View key={'sku_' + item.itemId} className={style.PartSearchResultCard}>
            <View padding={5} backgroundColor={'primary'} textAlign="start" as="ul">
              <View.Item as="li" key="item_part_number">
                <Text variant="display-2" color="secondary">
                  {t('part')} {item.partNumberId}
                </Text>
              </View.Item>
              <View.Item as="li" key="item_number">
                {t('sku')} <strong>{padTo9LeadingZeroes(stripLeadingZeroes(item.itemId))}</strong>
              </View.Item>
              <View.Item as="li" key="item_description">
                {t('description')} <strong>{item.description}</strong>
              </View.Item>
              <View>
                <View>
                  {item.slots?.map((itemSlot: ItemSlot) => {
                    return (
                      <View.Item key={`slot_${itemSlot.slotId}`}>
                        <View>
                          <View
                            backgroundColor="primary"
                            borderColor="accent"
                            className={style.PartSearchResultRow}
                          >
                            <View gap={2} direction="row">
                              <View.Item grow>
                                <View height="40px" justify="center" align="center" direction="row">
                                  {t('zone')} &nbsp; &nbsp;{' '}
                                  <strong>{computeZone(itemSlot.slotId)}</strong>
                                </View>
                              </View.Item>
                              <View.Item attributes={{ style: { margin: 0 } }}>
                                <View height="40px" justify="center" align="center" direction="row">
                                  |
                                </View>
                              </View.Item>
                              <View.Item grow>
                                <View height="40px" justify="center" align="center" direction="row">
                                  {t('aisle')}&nbsp; &nbsp;
                                  <strong>{computeAisle(itemSlot.slotId)}</strong>
                                </View>
                              </View.Item>
                              <View.Item attributes={{ style: { margin: 0 } }}>
                                <View height="40px" justify="center" align="center" direction="row">
                                  |
                                </View>
                              </View.Item>
                              <View.Item grow>
                                <View height="40px" justify="center" align="center" direction="row">
                                  {t('slot')} &nbsp; &nbsp;
                                  <strong>{computeSlot(itemSlot.slotId)}</strong>
                                </View>
                              </View.Item>
                              <View.Item attributes={{ style: { margin: 0 } }}>
                                <View height="40px" justify="center" align="center" direction="row">
                                  |
                                </View>
                              </View.Item>
                              <View.Item grow>
                                <View height="40px" justify="center" align="center" direction="row">
                                  {t('qty')} &nbsp; &nbsp; <strong>{itemSlot.quantity}</strong>
                                </View>
                              </View.Item>
                            </View>
                          </View>
                        </View>
                      </View.Item>
                    )
                  })}
                </View>
              </View>
            </View>
          </View>
        )
      })}
    </View>
  )
}
export default PartSearchCard
